import React, { useState, useEffect } from 'react';
import { Alert, Snackbar, Button, Chip, Stack } from '@mui/material';
import { CloudOff, CloudQueue, Sync, PendingActions } from '@mui/icons-material';
import { getPendingCount, syncAllPending } from '../utils/offlineSync';

export default function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingDetail, setPendingDetail] = useState({ punches: 0, claims: 0, leaves: 0, checkpoints: 0 });
  const [syncing, setSyncing] = useState(false);
  const [showSynced, setShowSynced] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online (no manual click needed)
      setTimeout(() => handleSync(), 1500);
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    updatePendingCount();
    const interval = setInterval(updatePendingCount, 30000);

    // Listen for sync completion from offlineSync.js
    const handleSyncComplete = () => updatePendingCount();
    window.addEventListener('offline-sync-complete', handleSyncComplete);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('offline-sync-complete', handleSyncComplete);
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePendingCount = async () => {
    try {
      const count = await getPendingCount();
      setPendingCount(count.total);
      setPendingDetail(count);
    } catch (err) {
      console.error('[OfflineIndicator] Failed to get pending count:', err);
    }
  };

  const handleSync = async () => {
    if (syncing || !navigator.onLine) return;
    setSyncing(true);
    try {
      const results = await syncAllPending();
      if (results) {
        const synced = results.punches + results.checkpoints + results.claims + results.leaves;
        if (synced > 0) {
          setSyncResult(results);
          setShowSynced(true);
          setTimeout(() => setShowSynced(false), 4000);
        }
      }
      await updatePendingCount();
    } catch (err) {
      console.error('[OfflineIndicator] Sync failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  // Offline banner — positioned below the app bar (64px), non-blocking
  if (!isOnline) {
    return (
      <Alert
        severity="warning"
        icon={<CloudOff />}
        sx={{
          position: 'fixed',
          top: 64, // Below the MUI AppBar
          left: 0,
          right: 0,
          zIndex: 1200, // Below AppBar (1100) + drawer but above content
          borderRadius: 0,
          boxShadow: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <span style={{ fontWeight: 500 }}>You're offline.</span>
          {pendingCount > 0 && (
            <Chip
              size="small"
              label={`${pendingCount} pending`}
              color="warning"
              variant="outlined"
              icon={<PendingActions />}
            />
          )}
          <span style={{ fontSize: '0.8em', opacity: 0.8 }}>
            Changes will sync automatically.
          </span>
        </Stack>
      </Alert>
    );
  }

  // Syncing indicator
  if (syncing) {
    return (
      <Alert
        severity="info"
        icon={<CloudQueue />}
        sx={{
          position: 'fixed',
          top: 64,
          left: 0,
          right: 0,
          zIndex: 1200,
          borderRadius: 0,
          boxShadow: 2,
        }}
      >
        Syncing pending changes...
      </Alert>
    );
  }

  // Synced confirmation
  if (showSynced && syncResult) {
    const parts = [];
    if (syncResult.punches > 0) parts.push(`${syncResult.punches} punch${syncResult.punches > 1 ? 'es' : ''}`);
    if (syncResult.claims > 0) parts.push(`${syncResult.claims} claim${syncResult.claims > 1 ? 's' : ''}`);
    if (syncResult.leaves > 0) parts.push(`${syncResult.leaves} leave${syncResult.leaves > 1 ? 's' : ''}`);
    if (syncResult.checkpoints > 0) parts.push(`${syncResult.checkpoints} checkpoint${syncResult.checkpoints > 1 ? 's' : ''}`);
    return (
      <Snackbar
        open={showSynced}
        autoHideDuration={4000}
        onClose={() => setShowSynced(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }} // Below app bar
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Synced: {parts.join(', ')}!
        </Alert>
      </Snackbar>
    );
  }

  // Show pending count with manual sync button (online but has pending items)
  if (pendingCount > 0) {
    return (
      <Alert
        severity="info"
        icon={<PendingActions />}
        action={
          <Button color="inherit" size="small" onClick={handleSync} startIcon={<Sync />}>
            Sync
          </Button>
        }
        sx={{
          position: 'fixed',
          top: 64,
          left: 0,
          right: 0,
          zIndex: 1200,
          borderRadius: 0,
          boxShadow: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <span>{pendingCount} pending change{pendingCount !== 1 ? 's' : ''}</span>
          <Stack direction="row" spacing={0.5}>
            {pendingDetail.punches > 0 && <Chip size="small" label={`${pendingDetail.punches} punch`} variant="outlined" />}
            {pendingDetail.claims > 0 && <Chip size="small" label={`${pendingDetail.claims} claim`} variant="outlined" />}
            {pendingDetail.leaves > 0 && <Chip size="small" label={`${pendingDetail.leaves} leave`} variant="outlined" />}
          </Stack>
        </Stack>
      </Alert>
    );
  }

  return null;
}
